<template>
    <div v-if="should_show_faq" class="mt-4">
        <strong>Frequently Asked Questions (FAQ)</strong>
        <div class="ml-4">
            <div v-if="is_birthday_message">
                <m-faq>
                    <template #question>When will this message be sent?</template>
                    <template #answer>
                        <div v-if="template_context === 'birthday-message'">
                            <p>
                                Birthday messages are sent based on the "Day to send relative to birthday" setting:
                            </p>
                            <ul>
                                <li><strong>Positive value:</strong> Sends the message that many days AFTER the birthday</li>
                                <li><strong>Zero (0):</strong> Sends the message ON the birthday</li>
                                <li><strong>Negative value:</strong> Sends the message that many days BEFORE the birthday</li>
                            </ul>
                        </div>
                        <div v-if="template_context === 'birthday-month-message'">
                            <p>
                                Birthday month messages are sent on the specified day of the month (1-27) to all customers with birthdays in that month.
                            </p>
                        </div>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>What's the difference between informational and promotional message types?</template>
                    <template #answer>
                        <p>
                            <strong>Informational messages</strong> are transactional in nature and provide important information about orders, shipments, reservations, or account updates. Examples include:
                        </p>
                        <ul>
                            <li>Order confirmations and shipping notifications</li>
                            <li>Appointment or reservation reminders</li>
                            <li>Account updates or security alerts</li>
                            <li>Customer service responses</li>
                        </ul>
                        <p>
                            <strong>Promotional messages</strong> are promotional in nature and advertise products, services, or events. Examples include:
                        </p>
                        <ul>
                            <li>Sales announcements and special offers</li>
                            <li>New product launches</li>
                            <li>Event invitations</li>
                            <li>Promotional campaigns</li>
                        </ul>
                    </template>
                </m-faq>
                <m-faq>
                    <template #question>Which integrations can provide birthday data?</template>
                    <template #answer>
                        This message can be triggered by the following integrations:
                        <m-integrations-that-support
                            concept="birthday"
                        />
                        <p></p>
                        <p>
                            For these messages to work, your customers must have birthdate information stored in one of these integrated systems.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>Why would I want to ignore certain birth dates?</template>
                    <template #answer>
                        <p>
                            The "Ignored Birth Dates" feature allows you to exclude specific dates from triggering birthday messages. This is useful for:
                        </p>
                        <ul>
                            <li>Excluding default or placeholder dates that some CRM systems automatically assign (like January 1st)</li>
                            <li>Avoiding sending messages on dates when you know your system contains test or dummy data</li>
                        </ul>
                        <p></p>
                        <p>
                            Any customer with a birthday matching an ignored date will not receive birthday messages. This filter only applies to customers with full birth date (including year) in our system.
                        </p>
                    </template>
                </m-faq>

                <m-faq v-if="template_context === 'birthday-message'">
                    <template #question>What does the "Day to send relative to birthday" parameter do?</template>
                    <template #answer>
                        <p>
                            For <strong>Birthday Messages</strong>, the "Delay Days" parameter allows you to adjust when the message is sent relative to the actual birthday:
                        </p>
                        <ul>
                            <li><strong>Positive value (e.g., 1):</strong> Sends the message that many days AFTER the birthday</li>
                            <li><strong>Zero (0):</strong> Sends the message ON the birthday</li>
                            <li><strong>Negative value (e.g., -1):</strong> Sends the message that many days BEFORE the birthday</li>
                        </ul>
                        <p>
                            For example, setting a delay of -1 would send birthday messages one day before the actual birthday, which is useful for sending "birthday eve" messages or ensuring customers receive messages before their special day.
                        </p>
                    </template>
                </m-faq>

                <m-faq v-if="template_context === 'birthday-month-message'">
                    <template #question>What does the "Day of month to send" parameter do?</template>
                    <template #answer>
                        <p>
                            For <strong>Birthday Month Messages</strong>, this parameter specifies the day of the month (1-27) when the message should be sent to all customers with birthdays in that month. We recommend setting this to 2 to ensure the message is sent early in the month before most birthdays occur.
                        </p>
                    </template>
                </m-faq>
            </div>

            <div v-if="is_second_generation_shipping_message">
                <m-faq>
                    <template #question>Which integrations can trigger this message?</template>
                    <template #answer>
                        This message can<sup>*</sup> be triggered by the following integrations:
                        <m-integrations-that-support
                            :concept="shipping_trigger_concept"
                        />
                        <p
                            class="mt-2"
                            v-if="template_context === 'shipping-new-tracking-number'"
                        >
                            * For an integration to trigger this message you must (1) be entering the tracking number of shipments into the integration and (2) the integration has to know the telephone number(s) of the package's recipient(s).
                        </p>
                        <p
                            class="mt-2"
                            v-else
                        >
                            * For an integration to trigger this message it must be tracking the package in question.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>What if I have multiple integrations turned on &mdash; will my customers receive duplicate shipping notifications?</template>
                    <template #answer>
                        <p>
                            RedChirp automatically de-duplicates duplicate messages. As a result, your customers <strong>will not</strong> receive duplicate messages about the same package, no matter how many integrations you have enabled.
                        </p>
                        <p>
                            <strong>Note:</strong> Some shipping events (e.g. out for delivery, delivery attempt failed, etc.) can occur multiple times in the course of delivering one packag.
                            For example, the same package may go out for delivery on one day, have the delivery attempt fail, and then go out for delivery again the next day.
                            In these cases, RedChirp will send automated message only one time per package per phone number each day.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>What about old events &mdash; will RedChirp sent messages about packages shipped a long time ago?</template>
                    <template #answer>
                        <p>
                            RedChirp will only send shipping related messages where the time of the event is today or yesterday.
                            Older events will simply be ignored. See the FAQ "Where does RedChirp read the scheduled ship date from?" to see where RedChirp gets this date from each integration source.
                        </p>
                        <p>
                            If RedChirp is aware of an event that generally happens later in the lifecycle of a shipment &mdash; First In Transit Event, Out for Delivery, Delivery Attempt Failed, Delivered, Available for Pickup, or Picked up &mdash; at the time it is preparing this message, RedChirp will not send this message.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>What phone numbers will receive this message?</template>
                    <template #answer>
                        <p>
                            RedChirp will send this text to every phone number that appears anywhere on the order (provided, of course, that the number has not opted out of texting).
                            If you do not want a specific phone number to receive these text messages — for example, the phone number of a corporate gifter who is included in many orders — use the
                            <a href="https://redchirp.com/how-to-exclude-recipients-from-your-automated-messages/" target="_blank">Excluded Recipients functionality.</a>
                        </p>
                        <p>
                            Every phone number that appears anywhere on the order, provided, of course, that the number has not opted out of texting.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>What happens when a shipment contains more than one package?</template>
                    <template #answer>
                        <p>
                            RedChirp's automated shipment messages are per tracking number so customers will receive a text message <i>per package</i>.
                        </p>
                        <p>
                            Each package is treated individually by design because packages can diverge from one another.
                        </p>
                    </template>
                </m-faq>

                <div v-if="template_context === 'shipping-new-tracking-number'">
                    <m-faq>
                        <template #question>Where does RedChirp read the scheduled ship date from?</template>
                        <template #answer>
                            <ul>
                            <li>Commerce7</li>
                            <ul>
                                <li>
                                    RedChirp reads the date that you select as the "Fulfillment Date" in the create fulfillment screen in Commerce7 (<a href="/app/help-images/c7-ship-date-1.png" target="_blank">picture</a>).
                                </li>
                                <li>
                                    For orders that are shipped: after being entered this date shows on the Fulfillment tab as the "Shipped on" date. (<a href="/app/help-images/c7-ship-date-2.png" target="_blank">picture</a>)
                                </li>
                            </ul>
                            <li>eCELLAR</li>
                            <ul>
                                <li>
                                    RedChirp reads the date that you enter as the date shipped
                                </li>
                            </ul>
                            <li>OrderPort</li>
                            <ul>
                                <li>
                                    RedChirp reads the date that you enter as the ship date
                                </li>
                            </ul>
                            <li>ShipCompliant</li>
                            <ul>
                                <li>
                                    RedChirp reads the "Actual Ship Date" field from ShipCompliant (<a href="/app/help-images/ship-compliant-ship-date.png" target="_blank">picture</a>)
                                </li>
                            </ul>
                            <li>ShipEngine</li>
                            <ul>
                                <li>
                                    RedChirp reads the carrier's actual label created time
                                </li>
                            </ul>
                            <li>WineCo</li>
                            <ul>
                                <li>
                                    RedChirp reads the carrier's actual label created time
                                </li>
                            </ul>
                            <li>WineDirect</li>
                            <ul>
                                <li>
                                    RedChirp reads the Shipped Date field from WineDirect
                                </li>
                            </ul>

                            <li>WineDirect Fulfillment</li>
                            <ul>
                                <li>
                                    RedChirp reads the carrier's actual label created time
                                </li>
                            </ul>
                            <li>Wineshipping</li>
                            <ul>
                                <li>
                                    If the "Requested Ship Date (RSD)" is set <span class="text-decoration-underline">and</span> in the future, then RedChirp uses the "Requested Ship Date"
                                </li>
                                <li>
                                    Otherwise, RedChirp reads the carrier's actual label created time
                                </li>
                            </ul>
                        </ul>
                        </template>
                    </m-faq>
                </div>
            </div>
            
            <div v-if="is_order_placed_message">
                <m-faq>
                    <template #question>What are the "Order Placed" message types and what are their differences?</template>
                    <template #answer>
                        <ul>
                            <li>Order Placed
                                <ul>
                                    <li>This is the standard order placed confirmation text message.</li>
                                    <li>It is designed to just confirm basic receipt of an order.</li>
                                    <li>This message is sent to <strong>all</strong> phone numbers on an order (including non-bill to numbers) that do not receive one of the other higher-priority messages.</li>
                                </ul>
                            </li>
                            <li>Order Placed: Request Opt In (uses Double Opt-In)
                                <ul>
                                    <li>This double opt in message is designed to provide reciept of an order and request messaging consent from customers.</li>
                                    <li>This message is <strong>only sent to bill to phone numbers</strong>.</li>
                                    <li>This includes starting a double opt-in workflow to confirm the customer's intent to receive messages from your organization.</li>
                                    <li>This can be used in conjunction with the "Order Placed" message to request messaging consent only from customers who are not yet opted in.</li>
                                </ul>
                            </li>
                            
                            <li>Order Placed: Request Confirmation and Opt-In (Uses Order Confirmation Form)
                                <ul>
                                    <li>This message is designed to request customers to confirm their order details via a web form.</li>
                                    <li>This message is <strong>only sent to bill to phone numbers</strong> and has the highest priority.</li>
                                    <li>It includes a link to an Order Confirmation form where customers can review and confirm their order information.</li>
                                    <li>Customers can decide to provide consent to receive text messages and emails from your organization as part of this flow.</li>
                                </ul>
                            </li>
                            
                        </ul>
                    </template>
                </m-faq>
                
                <m-faq data-validation-target="orderPlacedMultipleFaq">
                    <template #question>What happens if I have multiple "Order Placed" messages enabled?</template>
                    <template #answer>
                        <ul>
                            <li>Only <strong>one</strong> "Order Placed" message type should be sent <strong>per phone number</strong> for a single order.</li>
                            <li>If only one "Order Placed" message type is enabled, that message should be the one used for all applicable phone numbers.</li>
                            <li>
                                When multiple "Order Placed" automated message types are enabled, RedChirp first attempts to determine each phone number's <strong>role</strong> on the order (e.g., "Bill To", "Ship To", etc) and applies logic based on that role:
                                <ul class="mt-2">
                                    <li>
                                        <strong>For Bill To Phone Numbers:</strong>
                                        <ul>
                                            <li>
                                                <strong>Step 1: Check for Confirmation Request</strong>
                                                <ul>
                                                    <li>First, RedChirp checks if "Order Placed: Request Confirmation and Opt-In" is enabled.</li>
                                                    <li><strong>If YES:</strong> This message is sent, and <strong>no other "Order Placed" message will be sent to this phone number.</strong></li>
                                                </ul>
                                            </li>
                                            <li>
                                                <strong>Step 2: Check Consent Status (if no Confirmation was sent)</strong>
                                                <ul>
                                                    <li>If the Confirmation Request message was not used, RedChirp then checks the recipient's promotional consent.</li>
                                                    <li><strong>If promotional consent is NOT on file:</strong> The "Order Placed: Request Opt In" message should be sent (if enabled).</li>
                                                    <li><strong>If promotional consent IS granted:</strong> The standard "Order Placed" message should be sent (if enabled).</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                    <li class="mt-2">
                                        <strong>For All Other Phone Numbers:</strong>
                                        <ul>
                                            <li>These phone numbers are <strong>not</strong> eligible for "Request Confirmation" or "Request Opt-In" messages.</li>
                                            <li>They should <strong>only</strong> receive the standard "Order Placed" message (if it is enabled).</li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </template> 
                </m-faq>
                
                <m-faq>
                        <template #question>What are common "Order Placed" message type setups?</template>
                        <template #answer>  
                        While RedChirp should manage any combination of "Order Placed" message types, the most common setups are:                  
                        <ul>
                            <li>"Order Placed" alone</li>
                            <li>"Order Placed: Request Confirmation and Opt-In (Uses Order Confirmation Form)" alone</li>
                            <li>"Order Placed" together with "Order Placed: Request Opt In (uses Double Opt-In)"</li>
                        </ul>
                    </template>
                </m-faq>
            </div>

            <div v-if="template_context === 'order-placed-request-opt-in'">
                <m-faq>
                    <template #question>When will this message be sent?</template>
                    <template #answer>
                        RedChirp should send this message:
                        <ul>
                            <li>After RedChirp syncs a new order from your connected CRM/POS</li>
                            <li>Only for orders with a total of at least $1.00</li>
                            <li>At most once per week per phone number (weekly throttling)</li>
                        </ul>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>What happens when a customer replies to this message?</template>
                    <template #answer>
                        This message starts a double opt-in workflow:
                        <ul>
                            <li>When customers reply "YES", they should be granted promotional consent and can receive future promotional messages</li>
                            <li>When customers reply "STOP", they should be denied promotional consent and will not receive promotional messages</li>
                            <li>The system should track these responses and update consent preferences accordingly</li>
                        </ul>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>Who will receive this message?</template>
                    <template #answer>
                        <strong>Only bill to phone numbers</strong> associated with the order are eligible to receive this message. Use conditions to further target (e.g., POS vs. online orders).
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>Why didn't a message send for a small order?</template>
                    <template #answer>
                        Orders under $1.00 (including $0 and negative/refund totals) should not trigger this message.
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>How often can this message send to a customer?</template>
                    <template #answer>
                        To reduce noise, RedChirp should limit this message to once per week per phone number.
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>Can I send different content for POS vs. other order types?</template>
                    <template #answer>
                        Yes. Add a condition for order type (e.g., POS) with its own message content. Orders not matching conditions should use the default message content.
                    </template>
                </m-faq>
            </div>

            <div v-if="template_context === 'order-placed'">
                <m-faq>
                    <template #question>When will this message be sent?</template>
                    <template #answer>
                        RedChirp should send this message:
                        <ul>
                            <li>After RedChirp syncs a new order from your connected CRM/POS</li>
                            <li>For all order types (including club orders, if not filtered)</li>
                            <li>To all phone numbers on the order *unless* they are a bill to number that receives a higher-priority message (like Request Confirmation or Request Opt-In)</li>
                        </ul>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>Who will receive this message?</template>
                    <template #answer>
                        This message should be sent to <strong>all phone numbers</strong> on the order that do not receive one of the other "Order Placed" message types. It acts as the primary message for non bill to numbers and as a fallback for bill to numbers who have consent (and when Request Confirmation is not enabled). Use conditions to further target (e.g., POS vs. online orders).
                    </template>
                </m-faq>
            </div>

            <div v-if="template_context === 'order-placed-request-order-confirmation'">
                <m-faq>
                    <template #question>When will this message be sent?</template>
                    <template #answer>

                        RedChirp should send this message:
                        <ul>
                            <li>After RedChirp syncs a new order from your connected CRM/POS</li>
                            <li>For all order types (including club orders, if not filtered)</li>
                            <li>To bill to phone numbers regardless of consent status (it has the highest priority)</li>
                        </ul>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>How does this message work with Order Confirmation forms?</template>
                    <template #answer>
                        This message should include a unique link to your Order Confirmation form:
                        <ul>
                            <li>Each customer should receive a personalized link with their specific order details</li>
                            <li>The form should allow customers to review and confirm their order information</li>
                            <li>You must create and publish an Order Confirmation form before this message can be used</li>
                            <li>The form should be configured to use this automated message in the settings</li>
                        </ul>
                    </template>
                </m-faq>

                <m-faq>
                    <template #question>Who will receive this message?</template>
                    <template #answer>
                        <strong>Only bill to phone numbers</strong> associated with the order are eligible to receive this message. Use conditions to further target (e.g., POS vs. online orders).
                    </template>
                </m-faq>
            </div>

            <div v-if="template_context === 'order-fulfilled-follow-up'">
                <m-faq>
                    <template #question>Which integrations can trigger this message?</template>
                    <template #answer>
                        This message can be triggered by the following integrations:
                        <m-integrations-that-support
                            concept="can-trigger-order-follow-up"
                        />
                    </template>
                </m-faq>
                <m-faq>
                    <template #question>Commerce7: what precisely determines if/when this message will be sent?</template>
                    <template #answer>
                        <ul>
                            <li>
                                This message will be sent the specified number of days after the "Shipped Date" of the order
                            </li>
                            <li>
                                This message will not be sent if it has been cancelled before the indended follow up date.
                            </li>
                        </ul>
                    </template>
                </m-faq>
                <m-faq>
                    <template #question>OrderPort: what precisely determines if/when this message will be sent?</template>
                    <template #answer>
                        <ul>
                            <li>
                                This message will be sent the specified number of days after the "Shipped Date" of the order
                            </li>
                            <li>
                                This message will not be sent if it has been cancelled before the indended follow up date.
                            </li>
                        </ul>
                    </template>
                </m-faq>
                <m-faq>
                    <template #question>WineDirect: what precisely determines if/when this message will be sent?</template>
                    <template #answer>
                        <ul>
                            <li>
                                This message will be sent the specified number of days after the <i>first</i> of either the "Shipped Date" or the "Pickup Date"
                            </li>
                            <li>
                                This message will not be sent if it has been cancelled before the indended follow up date.
                            </li>
                        </ul>
                    </template>
                </m-faq>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AutoMessageFAQ',

    props: {
        template_context: {
            type: String,
            required: true
        },
        is_second_generation_shipping_message: {
            type: Boolean,
            default: false
        },
        shipping_trigger_concept: {
            type: String,
            default: null
        }
    },

    computed: {
        is_birthday_message() {
            return ['birthday-message', 'birthday-month-message'].includes(this.template_context);
        },
        is_order_placed_message() {
            return ['order-placed', 'order-placed-request-opt-in', 'order-placed-request-order-confirmation'].includes(this.template_context);
        },
        should_show_faq() {
            return this.is_birthday_message || 
                this.is_order_placed_message ||
                this.is_second_generation_shipping_message || 
                this.template_context === 'order-fulfilled-follow-up';
        }
    }
}
</script>